import { translations } from './translations';
import {
  DEFAULT_LANGUAGE,
  type SupportedLanguage,
  type TranslationKey,
} from './types';

type InterpolationValues = Record<string, string | number>;

type TranslationTree = Record<string, unknown>;

let currentLanguage: SupportedLanguage = DEFAULT_LANGUAGE;

const getNestedValue = (
  source: TranslationTree,
  key: string
): string | undefined => {
  const parts = key.split('.');
  let cursor: unknown = source;

  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') {
      return undefined;
    }

    cursor = (cursor as TranslationTree)[part];
  }

  return typeof cursor === 'string' ? cursor : undefined;
};

const interpolate = (template: string, values?: InterpolationValues): string => {
  if (!values) {
    return template;
  }

  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const value = values[key];
    return value === undefined ? '' : String(value);
  });
};

export const setCurrentLanguage = (language: SupportedLanguage): void => {
  currentLanguage = language;
};

export const getCurrentLanguage = (): SupportedLanguage => currentLanguage;

export const translate = (
  key: TranslationKey,
  values?: InterpolationValues,
  language: SupportedLanguage = currentLanguage
): string => {
  const translationSet = translations[language] || translations[DEFAULT_LANGUAGE];
  const fallbackSet = translations[DEFAULT_LANGUAGE];
  const template =
    getNestedValue(translationSet, key) || getNestedValue(fallbackSet, key);

  if (!template) {
    return key;
  }

  return interpolate(template, values);
};

export const translateUnsafe = (
  key: string,
  values?: InterpolationValues,
  language: SupportedLanguage = currentLanguage
): string => {
  const translationSet = translations[language] || translations[DEFAULT_LANGUAGE];
  const fallbackSet = translations[DEFAULT_LANGUAGE];
  const template =
    getNestedValue(translationSet, key) || getNestedValue(fallbackSet, key);

  if (!template) {
    return key;
  }

  return interpolate(template, values);
};

export const hasTranslation = (
  key: string,
  language: SupportedLanguage = currentLanguage
): boolean => {
  const translationSet = translations[language] || translations[DEFAULT_LANGUAGE];
  const fallbackSet = translations[DEFAULT_LANGUAGE];

  return Boolean(
    getNestedValue(translationSet, key) || getNestedValue(fallbackSet, key)
  );
};
